import { genericDomain } from '../features/domain'
import { env } from './constants'
import { logout } from '../features/auth/events'
import { path } from './path'
import { useParams } from 'react-router-dom'
import { apiCallCompleted } from './apiMetrics'

export type Request<Payload> = {
  url?: string
  method?: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH'
  body?: Payload
  headers?: Record<string, string>
  query?: Record<string, string>
  cookies?: string
  formData?: Payload
  baseUrl?: string
}

export type Response<Result> = {
  ok: boolean
  body: Result
  status: number
  headers: Record<string, string>
}

export type ResponseError<Result = object> = {
  code: number
  message: string
  details?: {
    code: number
    params: { [key: string]: string }
    msg: string
    validationErrors: {
      items: {
        msg: string
        field: keyof Result
      }[]
    }
  }
}

export function fx<Payload, Result>(intitalParams: Request<Payload>) {
  const effect = genericDomain.createEffect<
    Request<Payload>,
    Response<Result>,
    Response<ResponseError<Payload>>
  >()

  effect.use((params) => {
    const props = {
      ...intitalParams,
      ...params,
    }

    return request(props)
  })

  return effect
}

export async function request<Response>(params: Request<unknown>) {
  const { url, method = 'GET', ...options } = params

  const formData = new FormData()
  const headers = new Headers(options.headers)

  const query = queryToString(options.query)

  setHeaders(headers)

  let requestBody: string | undefined | FormData = undefined

  if (options.body) {
    setHeaders(headers, 'application/json; charset=utf-8')
    requestBody = JSON.stringify(options.body)
  }

  if (options.formData) {
    appendFormData(options.formData, formData)
    requestBody = formData
  }

  const startTime = performance.now()

  let response: globalThis.Response
  try {
    response = await fetch(
      (options.baseUrl ?? env.baseUrl) + `${url}${query}`,
      {
        method,
        body: requestBody,
        headers,
      },
    )
  } catch (error) {
    try {
      apiCallCompleted({
        url: url || '',
        method,
        duration: performance.now() - startTime,
        status: 0,
        timestamp: Date.now(),
      })
    } catch {
      // ignore metric errors
    }
    throw error
  }

  const duration = performance.now() - startTime

  try {
    apiCallCompleted({
      url: url || '',
      method,
      duration,
      status: response.status,
      timestamp: Date.now(),
    })
  } catch {
    // ignore metric errors
  }

  let responseBody: Response = {} as Response

  if (getContentType(response.headers, 'application/json')) {
    responseBody = await response.json()
  } else if (
    getContentType(response.headers, 'image/png') ||
    getContentType(response.headers, 'application/pdf')
  ) {
    const blob = await response.blob()
    const src = URL.createObjectURL(blob)

    responseBody = {
      src,
    } as Response
  } else {
    responseBody = (await response.text()) as Response
  }

  const responder = {
    url,
    ok: response.ok,
    body: responseBody,
    status: response.status,
    headers: toObject(response.headers),
  }

  if (response.ok) return responder
  // Централизованная обработка 401
  if (
    response.status === 401 &&
    window.location.pathname !== path.login.entry()
  ) {
    logout()
    localStorage.setItem('profile', JSON.stringify({}))
  }

  throw responder
}

function setHeaders(headers: Headers, type?: string): Headers {
  if (!headers.has('content-type')) {
    const accessToken = localStorage.getItem('accessToken')?.replaceAll(`"`, '')

    const hasAuthHeaders = headers.get('Authorization')

    if (!hasAuthHeaders && accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`)
    }
  }

  return headers
}

function queryToString(query: Record<string, string> | undefined): string {
  return Boolean(query) ? `?${new URLSearchParams(query).toString()}` : ''
}

function getContentType(headers: Headers, type: string): boolean {
  return headers.get('content-type')?.includes(type) ?? false
}

function toObject(headers: Headers): Record<string, string> {
  const target = {}
  headers.forEach((value, key) => {
    // @ts-ignore
    target[key] = value
  })
  return target
}

function appendFormData<Payload extends object>(
  body: Payload,
  formData: FormData,
): FormData {
  Object.entries(body).forEach(([key, value]) => {
    formData.append(key, value)
  })
  return formData
}
